/**
 * lib/housekeeping.ts
 * ------------------------------------------------------------------
 * THE NUMBERED SERIES, AND WHETHER ANY OF THEM HAS A HOLE IN IT.
 *
 * Nine series now run through this system — journals, invoices,
 * adjustments, vouchers, payment runs, purchase orders, goods received,
 * requisitions, petty-cash slips, debtor and creditor journals. Every
 * one is allocated inside the transaction that writes its document, so a
 * failed write cannot burn a number. That is the design, and this is the
 * check that it held.
 *
 * WHY A GAP MATTERS. "Where is PV/2026/0184?" is the first question an
 * auditor asks about a series, and there are exactly two answers: it was
 * cancelled and is still on file, or nobody knows. The second answer is
 * the one that turns a query into a finding, because a missing document
 * in a payment series is indistinguishable from one that was removed.
 *
 * A GAP IS NOT PROOF OF ANYTHING. A transaction that rolls back after
 * taking its number leaves a legitimate hole, and so does a document
 * genuinely deleted before this system forbade deletion. So gaps are
 * REPORTED, never repaired: renumbering to close one would rewrite
 * documents people already hold, which is worse than the gap.
 *
 * A COUNTER BEHIND ITS DOCUMENTS IS THE DANGEROUS CASE. Gaps waste
 * numbers; a counter that has fallen behind reissues them, and two
 * documents sharing a number is the one failure that cannot be untangled
 * afterwards.
 *
 * PURE. Numbers in, findings out.
 * ------------------------------------------------------------------ */

export interface SeriesSpec {
  key: string;
  /** What a person calls it. */
  label: string;
  /** The counter document id, e.g. 'purchaseOrders-2026'. */
  counterId: string;
  /** How a number reads, for the message. */
  sample: string;
}

export interface SeriesState {
  spec: SeriesSpec;
  /** What the counter says has been issued. */
  counter: number;
  /** The sequence numbers actually found on documents. */
  used: number[];
}

export type SeriesVerdict = 'sound' | 'gaps' | 'behind' | 'duplicates' | 'unused';

export interface SeriesFinding {
  key: string;
  label: string;
  verdict: SeriesVerdict;
  counter: number;
  highest: number;
  count: number;
  /** Missing numbers below the highest issued. */
  gaps: number[];
  /** Numbers appearing on more than one document. */
  duplicates: number[];
  /** What this means, in a sentence somebody can act on. */
  message: string;
}

/**
 * Check one series.
 *
 * Ordered by which fact matters most rather than by severity of number.
 * DUPLICATES outrank everything: two documents sharing a number is the
 * only failure here that cannot be untangled later. A counter BEHIND its
 * documents outranks gaps for the same reason — it is about to create
 * duplicates. Gaps come last, because they are usually nothing.
 */
export function checkSeries(state: SeriesState): SeriesFinding {
  const used = [...state.used].sort((a, b) => a - b);
  const unique = new Set(used);
  const duplicates = [...new Set(used.filter((n, i) => used.indexOf(n) !== i))];
  const highest = used.length ? used[used.length - 1] : 0;

  const gaps: number[] = [];
  for (let n = 1; n < highest; n += 1) {
    if (!unique.has(n)) gaps.push(n);
  }

  const base = {
    key: state.spec.key,
    label: state.spec.label,
    counter: state.counter,
    highest,
    count: unique.size,
    gaps,
    duplicates,
  };

  if (duplicates.length) {
    return {
      ...base,
      verdict: 'duplicates',
      message: `${duplicates.length} number${duplicates.length === 1 ? ' is' : 's are'} on more `
        + `than one document (${preview(duplicates)}). Two documents sharing a number cannot be `
        + 'told apart afterwards — this needs looking at before anything else.',
    };
  }

  if (highest > state.counter) {
    return {
      ...base,
      verdict: 'behind',
      message: `The counter says ${state.counter} but a document already carries ${highest}. `
        + 'The next document issued will REUSE a number that is already in use.',
    };
  }

  if (used.length === 0) {
    return {
      ...base,
      verdict: 'unused',
      message: state.counter > 0
        ? `The counter has reached ${state.counter} but no documents exist. Numbers were taken `
          + 'and the documents did not survive.'
        : 'Nothing has been issued in this series yet.',
    };
  }

  if (gaps.length) {
    return {
      ...base,
      verdict: 'gaps',
      message: `${gaps.length} number${gaps.length === 1 ? '' : 's'} missing below ${state.spec.sample.replace(/\d+$/, String(highest))} `
        + `(${preview(gaps)}). Usually a write that rolled back after taking its number. `
        + 'Not repaired: renumbering would rewrite documents people already hold.',
    };
  }

  return {
    ...base,
    verdict: 'sound',
    message: `${unique.size} issued, unbroken, and the counter agrees.`,
  };
}

const preview = (ns: number[]): string =>
  ns.slice(0, 6).join(', ') + (ns.length > 6 ? `, and ${ns.length - 6} more` : '');

/** The series worth a person's attention, most serious first. */
export function rankFindings(findings: SeriesFinding[]): SeriesFinding[] {
  const order: Record<SeriesVerdict, number> = {
    duplicates: 0, behind: 1, unused: 2, gaps: 3, sound: 4,
  };
  return [...findings].sort(
    (a, b) => order[a.verdict] - order[b.verdict] || a.label.localeCompare(b.label),
  );
}

export interface HousekeepingSummary {
  total: number;
  sound: number;
  needsAttention: number;
  /** True when nothing anywhere needs looking at. */
  allClear: boolean;
}

export function summarise(findings: SeriesFinding[]): HousekeepingSummary {
  const sound = findings.filter((f) => f.verdict === 'sound' || f.verdict === 'unused').length;
  return {
    total: findings.length,
    sound,
    needsAttention: findings.length - sound,
    allClear: findings.length > 0 && sound === findings.length,
  };
}

/**
 * Pull the sequence number out of a document reference.
 *
 * Every series in this system ends in its number — PV/2026/0184,
 * REQ/2026/0007, jnl_2026_0031. Taking the LAST run of digits rather
 * than a positional slice means a change of prefix or separator does not
 * silently start reporting every document as gap number one.
 */
export function seqOf(reference: string): number | null {
  const m = String(reference ?? '').match(/(\d+)(?!.*\d)/);
  return m ? Number(m[1]) : null;
}
